import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/supabase';
import { FaCopy, FaCheckCircle } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import './ViewProfile.css';

// Import default banner placeholder (animated)
import liquidLoading from '../../assets/gifs/liquid_loading.gif';
import iwintoLogo from '../../assets/svgs/iwinto.png';
import iwintoCircularLogo from '../../assets/svgs/logo circular2.png';

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) {
    // Default avatar SVG
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
  }
  
  // If already a full URL, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // If data URL, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;
  
  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[ViewProfile] Error resolving avatar URL:', e);
  }

  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K";
};

// Helper function to resolve banner URL
const resolveBannerUrl = (filename, userFolderId) => {
  if (!filename) return null;
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) return filename;
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  const path = `${userFolderId}/${filename}`;
  
  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[ViewProfile] Error resolving banner URL:', e);
  }

  return null;
};

export default function ViewProfile() {
  const { username, userId, shareId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  // const [statsData, setStatsData] = useState({
  //   posts: 0,
  //   likes: 0,
  //   donates: 0,
  //   followers: 0,
  //   following: 0,
  //   saves: 0,
  //   locked: 0
  // });
  // const [selectedStats, setSelectedStats] = useState(["posts", "likes", "donates"]);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [usernameCopied, setUsernameCopied] = useState(false);
  
  // Get translations - hook must be called unconditionally
  const { translations: tRaw } = useLanguage();
  const t = { downloadModal: tRaw?.downloadModal || {} };

  // Immediate debug log to verify component is mounting
  console.log('[ViewProfile] Component rendering!', {
    pathname: location.pathname,
    username,
    userId,
    shareId,
    allParams: { username, userId, shareId }
  });

  // Validate route - check if this looks like a ViewProfile route
  // Can be either /username (simple) or /@username/:userId/:shareId/u (legacy)
  const isValidViewProfileRoute = React.useMemo(() => {
    // Simple username route: just /username
    if (username && !userId) {
      return true;
    }
    // Legacy route: /@username/:userId/:shareId/u
    if (username && userId) {
      const pathStartsWithAt = location.pathname.startsWith('/@');
      const looksLikeUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
      const usernameHasAt = username.startsWith('@');
      return pathStartsWithAt || (looksLikeUUID && usernameHasAt);
    }
    return false;
  }, [username, userId, location.pathname]);

  // Debug logging
  useEffect(() => {
    console.log('[ViewProfile] Component mounted with params:', { 
      username, 
      userId, 
      shareId, 
      isValidViewProfileRoute,
      pathname: location.pathname,
      pathnameEndsWithU: location.pathname.endsWith('/u')
    });
    
    // Username-only routes are valid (simple /username format)
    if (!userId && !username) {
      console.warn('[ViewProfile] Missing both userId and username');
    }
  }, [username, userId, shareId, isValidViewProfileRoute, location.pathname]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[ViewProfile] Starting to load profile...', { userId, username, shareId: shareId || 'not provided' });

        // ShareId is optional - we ignore it completely for ViewProfile
        if (shareId) {
          console.log('[ViewProfile] ShareId provided but ignored:', shareId);
        }

        let profileData = null;
        let profileError = null;

        // Fetch profile by userId if provided, otherwise by username
        if (userId) {
          console.log('[ViewProfile] Fetching profile with user_id:', userId);
          const result = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', userId)
            .single();
          profileData = result.data;
          profileError = result.error;
        } else if (username) {
          // Clean username (remove @ if present)
          const cleanUsername = username.replace(/^@+/, '');
          console.log('[ViewProfile] Fetching profile with username:', cleanUsername);
          const result = await supabase
            .from('profiles')
            .select('*')
            .eq('username', cleanUsername)
            .single();
          profileData = result.data;
          profileError = result.error;
        } else {
          setError('Username or User ID is required');
          setLoading(false);
          return;
        }

        if (profileError) {
          console.error('[ViewProfile] Profile fetch error:', profileError);
          setError(`Profile not found: ${profileError.message}`);
          setLoading(false);
          return;
        }

        if (!profileData) {
          console.error('[ViewProfile] Profile data is null');
          setError('Profile not found');
          setLoading(false);
          return;
        }

        console.log('[ViewProfile] Profile found:', profileData.username);

        // Get the actual user_id from the loaded profile for stats queries
        // const actualUserId = profileData.user_id;

        // Get selected stats from profile.stats jsonb (same logic as Profile.jsx)
        // const selected = Array.isArray(profileData?.stats?.customStats)
        //   ? profileData.stats.customStats
        //   : Array.isArray(profileData?.stats?.selected)
        //   ? profileData.stats.selected
        //   : ["posts", "likes", "donates"];
        // setSelectedStats(selected.slice(0, 3));

        // Fetch actual counts from database using the actual user_id from profile
        // (Commented out as stats are not currently displayed)
        // // 1) Count posts for this user
        // const { count: postsCount, error: postsCountError } = await supabase
        //   .from('posts')
        //   .select('*', { count: 'exact', head: true })
        //   .eq('user_id', actualUserId)
        //   .eq('is_archived', false);

        // if (postsCountError) {
        //   console.error('[ViewProfile] Error counting posts:', postsCountError);
        // }
        // console.log('[ViewProfile] Posts count:', postsCount);

        // // 2) Count total likes received by this user's posts
        // const { count: likesCount, error: likesCountError } = await supabase
        //   .from('likes')
        //   .select('*, posts!inner(*)', { count: 'exact', head: true })
        //   .eq('posts.user_id', actualUserId);

        // if (likesCountError) {
        //   console.error('[ViewProfile] Error counting likes:', likesCountError);
        // }
        // console.log('[ViewProfile] Likes count:', likesCount);

        // // 3) Count supports/donations received (from transactions where user is receiver)
        // let supportsCount = 0;
        // const { count: supportsFromTransactions, error: supportsCountError } = await supabase
        //   .from('transactions')
        //   .select('*', { count: 'exact', head: true })
        //   .eq('receiver_id', actualUserId)
        //   .eq('flow', 'in')
        //   .eq('field', 'winicoins');

        // if (!supportsCountError && supportsFromTransactions !== null && supportsFromTransactions !== undefined) {
        //   supportsCount = supportsFromTransactions;
        // }

        // if (supportsCountError) {
        //   console.error('[ViewProfile] Error counting supports:', supportsCountError);
        // }
        // console.log('[ViewProfile] Supports count:', supportsCount);

        // // 4) Count saves on this user's posts
        // const { count: savesCount, error: savesCountError } = await supabase
        //   .from('saves')
        //   .select('*, posts!inner(*)', { count: 'exact', head: true })
        //   .eq('posts.user_id', actualUserId);
        
        // if (savesCountError) {
        //   console.error('[ViewProfile] Error counting saves:', savesCountError);
        // }
        // console.log('[ViewProfile] Saves count:', savesCount);

        // // 5) Count locked posts
        // const { count: lockedPostsCount, error: lockedError } = await supabase
        //   .from('posts')
        //   .select('*', { count: 'exact', head: true })
        //   .eq('user_id', actualUserId)
        //   .eq('is_locked', true)
        //   .eq('is_archived', false);
        
        // if (lockedError) {
        //   console.error('[ViewProfile] Error counting locked posts:', lockedError);
        // }
        // console.log('[ViewProfile] Locked posts count:', lockedPostsCount);

        // Set stats data - ensure we handle null/undefined properly and convert to numbers
        // const stats = {
        //   posts: Number(postsCount) || 0,
        //   likes: Number(likesCount) || 0,
        //   donates: Number(supportsCount) || 0,
        //   followers: Number(profileData.follower_count) || 0,
        //   following: Number(profileData.following_count) || 0,
        //   saves: Number(savesCount) || 0,
        //   locked: Number(lockedPostsCount) || 0
        // };
        
        console.log('[ViewProfile] Setting stats data (commented out)');
        console.log('[ViewProfile] Profile follower_count:', profileData.follower_count);
        console.log('[ViewProfile] Profile following_count:', profileData.following_count);
        // setStatsData(stats);

        setProfile(profileData);
        console.log('[ViewProfile] Profile loaded successfully');
      } catch (err) {
        console.error('[ViewProfile] Error loading profile:', err);
        setError(`Failed to load profile: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Load profile if we have either userId or username
    if (userId || username) {
      loadProfile();
    } else {
      setError('Username or User ID is required');
      setLoading(false);
    }
  }, [userId, username, shareId]);

  // const handleInteraction = (type) => {
  //   setShowDownloadModal(true);
  // };

  const getDownloadUrl = () => {
    const appStoreUrl = 'https://apps.apple.com/ao/app/iwinto/id6482291656';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.winto.app';

    if (typeof navigator === 'undefined') {
      return appStoreUrl;
    }

    const possibleOpera =
      typeof window !== 'undefined' && window.opera ? window.opera : '';
    const userAgent =
      navigator.userAgent || navigator.vendor || possibleOpera || '';
    const isAndroid = /android/i.test(userAgent);
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isAndroid) {
      return playStoreUrl;
    }

    if (isIOS) {
      return appStoreUrl;
    }

    return appStoreUrl;
  };

  const handleDownloadClick = async () => {
    try {
      // Copy username to clipboard
      const usernameToCopy = profile?.username || username;
      if (usernameToCopy) {
        await navigator.clipboard.writeText(usernameToCopy);
        setUsernameCopied(true);
        // Keep notification visible permanently (don't reset)
      }
    } catch (err) {
      console.error('[ViewProfile] Failed to copy username:', err);
    }

    // Open the appropriate app store
    const url = getDownloadUrl();
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Fullscreen image handlers (same as Profile.jsx)
  const openFullscreen = (imageSrc) => {
    if (imageSrc) {
      setFullscreenImage(imageSrc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    // Restore body scroll when modal is closed
    document.body.style.overflow = 'unset';
  };

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && fullscreenImage) {
        closeFullscreen();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [fullscreenImage]);

  // Cleanup body scroll on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // If the authenticated user is viewing their own profile, hide public-only actions
  const isOwnProfile =
    !!currentUser?.id &&
    !!profile?.user_id &&
    currentUser.id === profile.user_id;

  // If authenticated user opens their own public link, redirect to full /profile page
  useEffect(() => {
    if (isOwnProfile) {
      navigate('/profile', { replace: true });
    }
  }, [isOwnProfile, navigate]);

  if (loading) {
    return (
      <div className="view-profile-container">
        <div className="view-profile-loading">
          <p>Loading profile...</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            {/* Debug: username={username}, userId={userId}, shareId={shareId} */}
          </p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="view-profile-container">
        <div className="view-profile-error">
          <p>{error || 'Profile not found'}</p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
            Debug: username={username}, userId={userId}, shareId={shareId}
          </p>
        </div>
      </div>
    );
  }

  const avatarUrl = resolveAvatarUrl(profile.avatar_url, profile.user_id);
  // Use animated placeholder if no banner_url
  const bannerUrl = resolveBannerUrl(profile.banner_url, profile.user_id) || liquidLoading;

  return (
    <div className="view-profile-container">
      <div className="view-profile-navbar">
        <div className="view-profile-navbar-inner">
          <img
            src={iwintoLogo}
            alt="iWinto"
            className="view-profile-navbar-logo"
          />
          <img
            src={iwintoCircularLogo}
            alt="iWinto icon"
            className="view-profile-navbar-right-icon"
          />
        </div>
      </div>
      <div className="view-profile-content">
        {/* Banner Section - Always show (with placeholder if no banner) */}
        <div className="view-profile-banner-wrapper">
          <div 
            className="view-profile-banner"
            style={{ backgroundImage: `url(${bannerUrl})`, cursor: 'pointer' }}
            onClick={() => openFullscreen(bannerUrl)}
          />
        </div>

        {/* Profile Info Section */}
        <div className="view-profile-info">
          {/* Avatar */}
          <div 
            className="view-profile-avatar-wrapper"
            onClick={() => openFullscreen(avatarUrl)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={avatarUrl}
              alt={profile.display_name || profile.username}
              className="view-profile-avatar"
            />
          </div>

          {/* Username and Display Name */}
          <div className="view-profile-header">
            <h2 className="view-profile-username">
              {profile.display_name || profile.username || 'User'}
            </h2>
            {/* {profile.display_name && profile.username && (
              <p className="view-profile-handle">@{profile.username}</p>
            )} */}
            {profile.profession && (
              <p className="view-profile-profession">{profile.profession}</p>
            )}
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="view-profile-bio">
              <p>{profile.bio}</p>
            </div>
          )}

          {/* Stats */}
          {/* <div className="view-profile-stats">
            {selectedStats.map((key) => {
              const labels = {
                posts: "posts",
                likes: "likes",
                donates: "donates",
                followers: "followers",
                following: "following",
                saves: "saves",
                locked: "locked content",
              };
              const values = {
                posts: Number(statsData.posts) || 0,
                likes: Number(statsData.likes) || 0,
                donates: Number(statsData.donates) || 0,
                followers: Number(statsData.followers) || 0,
                following: Number(statsData.following) || 0,
                saves: Number(statsData.saves) || 0,
                locked: Number(statsData.locked) || 0,
              };
              const value = values[key] ?? 0;
              return (
                <div key={key} className="view-profile-stat">
                  <span className="view-profile-stat-value">{value}</span>
                  <span className="view-profile-stat-label">{labels[key] || key}</span>
                </div>
              );
            })}
          </div> */}

          {/* Interaction Buttons - hide for own profile */}
          {/* {!isOwnProfile && (
            <div className="view-profile-actions">
              <button
                className="view-profile-action-btn"
                onClick={() => handleInteraction('follow')}
                aria-label="Follow"
              >
                <FaUserPlus />
                <span>Follow</span>
              </button>
              <button
                className="view-profile-action-btn"
                onClick={() => handleInteraction('message')}
                aria-label="Message"
              >
                <FaComment />
                <span>Message</span>
              </button>
              <button
                className="view-profile-action-btn"
                onClick={() => handleInteraction('donate')}
                aria-label="Donate"
              >
                <FaGift />
                <span>Donate</span>
              </button>
            </div>
          )} */}

          {/* Download App Section - hide for own profile */}
          {!isOwnProfile && (
            <div className="view-profile-download-section">
              {usernameCopied && (
                <div className="view-profile-copy-notification">
                  <FaCheckCircle />
                  <span>Username copied!</span>
                </div>
              )}
              
              <div className="view-profile-download-content">
                {/* <div className="view-profile-download-icon">
                  {(() => {
                    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
                    const isAndroid = /android/i.test(userAgent);
                    const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                                  (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                    
                    if (isAndroid) return <FaGooglePlay />;
                    if (isIOS) return <FaApple />;
                    return <FaApple />;
                  })()}
                </div> */}
                <div className="view-profile-download-info">
                  {/* <h3 className="view-profile-download-title">See {profile?.display_name || profile?.username}'s Posts</h3> */}
                  
                </div>
              </div>
              
              <button
                className="view-profile-download-btn"
                onClick={handleDownloadClick}
                aria-label="Download the app and copy username"
              >
                <FaCopy />
                <span>Join With Me</span>
              </button>
              <p className="view-profile-download-text">
                    Get the iWinto app to view posts, follow, and interact!
                  </p>
            </div>
          )}
        </div>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="view-profile-modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="view-profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="view-profile-modal-title">
              {t?.downloadModal?.title || "Want to interact with this profile?"}
            </h2>
            <p className="view-profile-modal-subtitle">
              {t?.downloadModal?.subtitle || "Download the iWinto app to follow, message, and donate!"}
            </p>
            <div className="view-profile-store-buttons">
              <button
                type="button"
                className="view-profile-store-btn"
                onClick={handleDownloadClick}
              >
                {t?.downloadModal?.downloadApp || "Download the app"}
              </button>
            </div>
            <button
              className="view-profile-modal-close"
              onClick={() => setShowDownloadModal(false)}
            >
              {t?.downloadModal?.close || "Close"}
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div className="view-profile-fullscreen-modal" onClick={closeFullscreen}>
          <div className="view-profile-fullscreen-content">
            <button className="view-profile-fullscreen-close" onClick={closeFullscreen}>
              ×
            </button>
            <div className="view-profile-fullscreen-frame" style={{ '--media-url': `url(${fullscreenImage})` }}>
              <img 
                src={fullscreenImage} 
                alt="Fullscreen" 
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

